/* eslint-disable */
import { Eye, Zap, Download, Copy, Shield, ShieldCheck, UserCheck, AlertTriangle, CheckCircle2, XCircle, Search, RefreshCw, Key, Building2, Users, Database, Clock, HardDrive, Check, Pause, Play, AlertCircle } from "lucide-react";
import React, { useState, useEffect } from "react";
import axios from "axios";
import CurrencySelector from "../components/CurrencySelector";
import { useCurrency } from "../context/CurrencyContext";
import { API_BASE } from "../config/apiConfig";

const BASE_URL = API_BASE || "http://localhost:8000";

export default function SuperAdminConsole({ handleLogout, currentUser }) {
  const { formatPrice } = useCurrency();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [toastMessage, setToastMessage] = useState("");
  const [theme, setTheme] = useState("light");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [timeFilter, setTimeFilter] = useState("MONTHLY");

  // Data States (Loaded Dynamically)
  const [analytics, setAnalytics] = useState({
    total_organizations: 0,
    active_organizations: 0,
    suspended_organizations: 0,
    total_users: 0,
    active_users: 0,
    online_users: 0,
    total_projects: 0,
    total_products: 0,
    total_test_cases: 0,
    total_manual_test_cases: 0,
    total_automated_test_cases: 0,
    total_executions: 0,
    running_executions: 0,
    failed_executions: 0,
    passed_executions: 0,
    pass_rate: 0,
    total_ai_requests: 0,
    total_tokens_consumed: 0,
    ai_cost_estimation: 0,
    storage_used: 0,
    storage_available: 0,
    active_licenses: 0,
    expiring_licenses: 0,
    plan_breakdown: {},
    monthly_revenue: 0.0,
    pending_support_requests: 0,
    recent_audit: []
  });

  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [rolesMatrix, setRolesMatrix] = useState({});

  // Filter & Search States
  const [globalSearch, setGlobalSearch] = useState("");

  // Modals
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [overviewOrg, setOverviewOrg] = useState(null);
  const [showPasswordsMap, setShowPasswordsMap] = useState({});
  const [overviewUserSearch, setOverviewUserSearch] = useState("");

  const [showOrgModal, setShowOrgModal] = useState(false);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgModalError, setOrgModalError] = useState("");
  const [showProvisionedModal, setShowProvisionedModal] = useState(false);
  const [provisionedData, setProvisionedData] = useState(null);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [addonTargetOrg, setAddonTargetOrg] = useState(null);
  const [addonType, setAddonType] = useState("users");
  const [addonQuantity, setAddonQuantity] = useState(5);
  const [addonNotes, setAddonNotes] = useState("");

  // Role Management Modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  // API Call Loaders
  const fetchAnalytics = async (force = false) => {
    try {
      const url = force ? `${BASE_URL}/api/super-admin/analytics?force_refresh=true` : `${BASE_URL}/api/super-admin/analytics`;
      const res = await axios.get(url);
      if (res.data && res.data.success) setAnalytics(res.data.metrics);
    } catch (e) { console.error("Analytics load error:", e); }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/super-admin/organizations`);
      if (res.data && res.data.success) {
        setOrganizations(res.data.organizations || []);
        if (overviewOrg) {
          const updated = (res.data.organizations || []).find(o => o.id === overviewOrg.id);
          if (updated) setOverviewOrg(updated);
        }
      }
    } catch (e) { console.error("Orgs load error:", e); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/super-admin/users`);
      if (res.data && res.data.success) setUsers(res.data.users || []);
    } catch (e) { console.error("Users load error:", e); }
  };

  const fetchAddonLogs = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/super-admin/tenant-lifecycle/logs`);
      if (res.data && res.data.success) setAuditLogs(res.data.logs || []);
    } catch (e) { console.error("Addon logs load error:", e); }
  };

  const fetchRolesMatrix = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/super-admin/role-permissions`);
      if (res.data && res.data.success) setRolesMatrix(res.data.matrix || {});
    } catch (e) { console.error("Role permissions load error:", e); }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/super-admin/subscriptions`);
      if (res.data && res.data.success) setSubscriptions(res.data.plans || []);
    } catch (e) { console.error("Subscriptions load error:", e); }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchOrganizations();
    fetchUsers();
    fetchAddonLogs();
    fetchRolesMatrix();
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "organizations") fetchOrganizations();
    else if (activeTab === "users") fetchUsers();
    else if (activeTab === "roles") fetchRolesMatrix();
    else if (activeTab === "billing") fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Handlers
  const handleDownloadTenantBackup = async (orgId) => {
    try {
      showToast("Preparing tenant data backup...");
      const res = await axios.get(`${BASE_URL}/api/super-admin/organizations/${orgId}/backups/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tenant_${orgId}_backup.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast("Tenant backup downloaded successfully");
    } catch (e) {
      showToast("Failed to download tenant backup");
    }
  };

  const handleSuspendOrg = async (org) => {
    if (!window.confirm(`Are you sure you want to suspend organization "${org.name}"?`)) return;
    try {
      const res = await axios.post(`${BASE_URL}/api/super-admin/organizations/${org.id}/suspend`, { reason: "Admin Action" });
      if (res.data && res.data.success) {
        showToast("Organization suspended successfully");
        fetchOrganizations();
        fetchAnalytics(true);
      } else {
        showToast(`❌ ${res.data?.error || "Failed to suspend organization"}`);
      }
    } catch (e) {
      showToast("❌ Failed to suspend organization");
    }
  };

  const handleUnsuspendOrg = async (org) => {
    if (!window.confirm(`Are you sure you want to reactivate organization "${org.name}"?`)) return;
    try {
      const res = await axios.post(`${BASE_URL}/api/super-admin/organizations/${org.id}/unsuspend`);
      if (res.data && res.data.success) {
        showToast("✅ Organization reactivated successfully");
        fetchOrganizations();
        fetchAnalytics(true);
      } else {
        showToast(`❌ ${res.data?.error || "Failed to unsuspend organization"}`);
      }
    } catch (e) {
      showToast("❌ Failed to reactivate organization");
    }
  };

  const handleResetPwd = async (email) => {
    if (!window.confirm(`Send password reset email to ${email}?`)) return;
    try {
      const res = await axios.put(`${BASE_URL}/api/super-admin/users/${encodeURIComponent(email)}/reset-password`, { password: "TempReset@123" });
      if (res.data && res.data.success) {
        showToast(`✅ Password reset link sent to ${email}`);
      } else {
        showToast(`❌ ${res.data?.error || "Failed to send reset link"}`);
      }
    } catch (e) {
      showToast("❌ Failed to reset password");
    }
  };

  const handleTogglePermission = async (roleName, permKey, currentVal) => {
    const newVal = !currentVal;
    // Optimistic UI update
    setRolesMatrix(prev => ({
      ...prev,
      [roleName]: {
        ...(prev[roleName] || {}),
        [permKey]: newVal
      }
    }));

    try {
      const res = await axios.post(`${BASE_URL}/api/super-admin/role-permissions`, {
        role_name: roleName,
        permission_key: permKey,
        allowed: newVal
      });
      if (res.data && res.data.success) {
        showToast(`✅ ${roleName} permission updated`);
      } else {
        showToast(`❌ Failed to update permission`);
        fetchRolesMatrix();
      }
    } catch (e) {
      showToast("❌ Error updating permission");
      fetchRolesMatrix();
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    try {
      const res = await axios.post(`${BASE_URL}/api/super-admin/roles`, {
        role_name: newRoleName.trim(),
        permissions: {
          view_reports: true,
          create_test: true,
          run_test: true
        }
      });
      if (res.data && res.data.success) {
        showToast(`🎉 Custom Role '${newRoleName.trim()}' created!`);
        setNewRoleName("");
        setShowRoleModal(false);
        fetchRolesMatrix();
      } else {
        alert("Error creating role: " + (res.data?.error || "Failed to create role"));
      }
    } catch (e) { alert("Failed to create custom role."); }
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm(`Are you sure you want to delete role '${roleName}'?`)) return;
    try {
      const res = await axios.delete(`${BASE_URL}/api/super-admin/roles/${encodeURIComponent(roleName)}`);
      if (res.data && res.data.success) {
        showToast(`🗑️ Role '${roleName}' removed`);
        fetchRolesMatrix();
      } else {
        showToast(`❌ ${res.data?.error || "Failed to delete role"}`);
      }
    } catch (e) { showToast("❌ Failed to delete role"); }
  };

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    setOrgModalError("");
    const data = {
      name: e.target.name.value,
      owner_email: e.target.owner_email.value,
      subscription_plan: e.target.subscription_plan.value,
      storage_limit_gb: parseInt(e.target.storage_limit_gb.value),
      ai_token_limit: parseInt(e.target.ai_token_limit.value),
      user_limit: parseInt(e.target.user_limit.value),
      execution_limit: parseInt(e.target.execution_limit.value)
    };
    try {
      let res = currentOrg
        ? await axios.put(`${BASE_URL}/api/super-admin/organizations/${currentOrg.id}`, data)
        : await axios.post(`${BASE_URL}/api/super-admin/organizations`, data);
      if (res.data && res.data.success) {
        if (!currentOrg && res.data.temp_password) {
          setProvisionedData({
            name: data.name,
            owner_email: data.owner_email,
            tenant_url: res.data.tenant_url,
            temp_password: res.data.temp_password,
            invitation_sent: res.data.invitation_sent
          });
          setShowProvisionedModal(true);
        } else {
          showToast(currentOrg ? "Organization limits updated!" : "New Organization provisioned!");
        }
        setShowOrgModal(false);
        setOrgModalError("");
        fetchOrganizations();
        fetchAnalytics(true);
        fetchUsers();
      } else {
        setOrgModalError(res.data?.error || "Failed to save organization.");
      }
    } catch (e) { setOrgModalError(e.response?.data?.error || e.message || "Failed to save organization."); }
  };

  const handleApplyAddon = async (e) => {
    e.preventDefault();
    if (!addonTargetOrg) return;
    try {
      const res = await axios.post(`${BASE_URL}/api/super-admin/organizations/${addonTargetOrg.id}/addon`, {
        addon_type: addonType,
        quantity: parseInt(addonQuantity),
        notes: addonNotes
      });
      if (res.data && res.data.success) {
        showToast(`✨ ${res.data.message}`);
        setShowAddonModal(false);
        fetchOrganizations();
        fetchAnalytics(true);
        fetchUsers();
      } else {
        alert("Error: " + (res.data ? res.data.error : "Failed to apply add-on"));
      }
    } catch (e) { alert("Failed to apply add-on package."); }
  };

  // Filtered lists
  const filteredOrgs = organizations.filter(o =>
    !globalSearch || o.name.toLowerCase().includes(globalSearch.toLowerCase()) || (o.owner_email && o.owner_email.toLowerCase().includes(globalSearch.toLowerCase()))
  );

  const filteredUsers = users.filter(u =>
    !globalSearch || u.name.toLowerCase().includes(globalSearch.toLowerCase()) || u.email.toLowerCase().includes(globalSearch.toLowerCase()) || (u.organization && u.organization.toLowerCase().includes(globalSearch.toLowerCase()))
  );

  // Dynamic Subscription Splits Calculations
  const planCounts = {};
  organizations.forEach(o => {
    const p = o.subscription_plan || "Free";
    planCounts[p] = (planCounts[p] || 0) + 1;
  });
  const totalOrgs = organizations.length || analytics.total_organizations || 0;

  const permissionKeys = [
    { key: "delete_project", label: "Delete Projects" },
    { key: "purge_test_runs", label: "Purge Logs" },
    { key: "edit_credentials", label: "Edit Credentials" },
    { key: "manage_team", label: "Manage Team" },
    { key: "create_test", label: "Create Tests" },
    { key: "run_test", label: "Run Tests" },
    { key: "view_reports", label: "View Reports" },
    { key: "manage_organizations", label: "Tenant Admin" },
    { key: "manage_billing", label: "Subscriptions" },
    { key: "manage_security", label: "Security & MFA" }
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: theme === "dark" ? "#0f172a" : "#f8fafc", color: theme === "dark" ? "#f8fafc" : "#0f172a", fontFamily: "'Inter', 'Outfit', sans-serif" }}>
      {/* Toast Notification Banner */}
      {toastMessage && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 9999, backgroundColor: "#ec4899", color: "#fff", padding: "12px 24px", borderRadius: "10px", boxShadow: "0 10px 25px rgba(236,72,153,0.3)", fontWeight: "600", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px", animation: "slideIn 0.3s ease-out" }}>
          <span>✨</span> {toastMessage}
        </div>
      )}

      {/* 1. LEFT SIDEBAR NAVIGATION */}
      <aside style={{ width: sidebarCollapsed ? "80px" : "240px", backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRight: theme === "dark" ? "1px solid #334155" : "1px solid #f1f5f9", display: "flex", flexDirection: "column", transition: "all 0.3s ease", zIndex: 100, flexShrink: 0 }}>
        {/* Brand Header */}
        <div style={{ height: "64px", display: "flex", alignItems: "center", padding: "0 20px", backgroundColor: "#ec4899", color: "#ffffff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src="/qintra_icon.png" alt="Qintra" style={{ width: "22px", height: "22px", objectFit: "contain" }} />
            </div>
            {!sidebarCollapsed && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "18px", fontWeight: "800", letterSpacing: "-0.5px" }}>Qintra.</span>
                <span style={{ fontSize: "10px", opacity: 0.85, textTransform: "uppercase", fontWeight: "700" }}>SuperAdmin Console</span>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Nav Items */}
        <nav style={{ padding: "16px 12px", flex: 1, overflowY: "auto" }}>
          {[
            { id: "dashboard", label: "Dashboard", icon: "📊" },
            { id: "organizations", label: "Organizations", icon: "🏢", count: organizations.length },
            { id: "users", label: "User Directory", icon: "👥", count: users.length },
            { id: "roles", label: "Roles & Permissions", icon: "🛡️", count: Object.keys(rolesMatrix).length },
            { id: "billing", label: "Subscriptions & Billing", icon: "💳" },
            { id: "apikeys", label: "API Keys & Webhooks", icon: "🔑" },
            { id: "security", label: "Security & MFA", icon: "🔒" },
            { id: "backup", label: "System Backups", icon: "📦" },
            { id: "aiproviders", label: "AI Intelligence", icon: "🤖" },
            { id: "documentation", label: "Documentation", icon: "📚" },
            { id: "audit", label: "Audit Logs", icon: "📜" }
          ].map(item => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justify: sidebarCollapsed ? "center" : "space-between",
                  padding: "12px 14px",
                  marginBottom: "4px",
                  borderRadius: "10px",
                  border: "none",
                  backgroundColor: isActive ? (theme === "dark" ? "#334155" : "#fdf2f8") : "transparent",
                  color: isActive ? "#ec4899" : (theme === "dark" ? "#cbd5e1" : "#475569"),
                  fontWeight: isActive ? "700" : "500",
                  fontSize: "13.5px",
                  cursor: "pointer",
                  transition: "all 0.15s ease"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontSize: "18px" }}>{item.icon}</span>
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </div>
                {!sidebarCollapsed && item.count !== undefined && (
                  <span style={{ backgroundColor: isActive ? "#ec4899" : "#f1f5f9", color: isActive ? "#ffffff" : "#64748b", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                    {item.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer Logout */}
        <div style={{ padding: "16px", borderTop: theme === "dark" ? "1px solid #334155" : "1px solid #f1f5f9" }}>
          <button
            onClick={handleLogout}
            style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #f43f5e", backgroundColor: "transparent", color: "#f43f5e", fontWeight: "600", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
          >
            <span>🚪</span> {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* 2. MAIN CONTENT AREA */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflowY: "auto" }}>
        {/* Top Header Bar */}
        <header style={{ height: "64px", backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderBottom: theme === "dark" ? "1px solid #334155" : "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", sticky: "top", zIndex: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>☰</button>
            <div>
              <h2 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Overview
              </h2>
              <span style={{ fontSize: "11.5px", color: "#64748b" }}>System Platform Control & Workspace Administration</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {/* Global Search */}
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Search anything..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                style={{ padding: "8px 14px 8px 36px", borderRadius: "20px", border: "1px solid #cbd5e1", fontSize: "12.5px", width: "220px", outline: "none", backgroundColor: theme === "dark" ? "#0f172a" : "#f8fafc", color: "inherit" }}
              />
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "14px" }}>🔍</span>
            </div>

            {/* Currency Selector */}
            <CurrencySelector />

            {/* Theme Switcher */}
            <button
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer" }}
              title="Toggle Theme"
            >
              {theme === "light" ? "🌙" : "☀️"}
            </button>

            {/* Profile Avatar */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingLeft: "12px", borderLeft: "1px solid #e2e8f0" }}>
              <div style={{ position: "relative" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "#ec4899", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700" }}>
                  {(currentUser?.name || "SA").slice(0, 2).toUpperCase()}
                </div>
                <span style={{ position: "absolute", bottom: "0", right: "0", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#10b981", border: "2px solid #fff" }}></span>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "13px", fontWeight: "700" }}>{currentUser?.name || "Platform Owner"}</span>
                <span style={{ fontSize: "11px", color: "#ec4899", fontWeight: "600" }}>Super Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Body Container */}
        <main style={{ padding: "24px", flex: 1 }}>
          {/* TAB 1: DASHBOARD */}
          {activeTab === "dashboard" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {/* TOP ROW: Main Spline Chart Card & Subscription Donut Chart Card */}
              <div style={{ display: "grid", gridTemplateColumns: "2.2fr 1fr", gap: "24px" }}>
                {/* Main Spline Area Chart Card */}
                <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>Platform Performance & Revenue</h3>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Overview of Execution & Earnings Trends</span>
                    </div>

                    {/* Time Filter Tabs */}
                    <div style={{ display: "flex", gap: "4px", backgroundColor: theme === "dark" ? "#0f172a" : "#f1f5f9", padding: "4px", borderRadius: "10px" }}>
                      {["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].map(tf => (
                        <button
                          key={tf}
                          onClick={() => setTimeFilter(tf)}
                          style={{
                            padding: "6px 14px",
                            borderRadius: "8px",
                            border: "none",
                            fontSize: "11px",
                            fontWeight: "700",
                            cursor: "pointer",
                            backgroundColor: timeFilter === tf ? "#ec4899" : "transparent",
                            color: timeFilter === tf ? "#ffffff" : "#64748b"
                          }}
                        >
                          {tf}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Earnings Metric & SVG Chart Body */}
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "20px", alignItems: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div>
                        <span style={{ fontSize: "28px", fontWeight: "800", color: "#ec4899" }}>{formatPrice(analytics.monthly_revenue || 0)}</span>
                        <span style={{ display: "block", fontSize: "12px", color: "#64748b", fontWeight: "600" }}>Monthly Platform Revenue</span>
                      </div>
                      <div>
                        <span style={{ fontSize: "22px", fontWeight: "800" }}>{analytics.total_executions || 0}</span>
                        <span style={{ display: "block", fontSize: "12px", color: "#64748b", fontWeight: "600" }}>Test Executions Run</span>
                      </div>
                      <button onClick={() => fetchAnalytics(true)} style={{ backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "10px 16px", borderRadius: "10px", fontWeight: "700", fontSize: "12px", cursor: "pointer", boxShadow: "0 4px 12px rgba(236,72,153,0.3)" }}>
                        🔄 Refresh Stats
                      </button>
                    </div>

                    {/* Smooth Area Spline SVG Graphic */}
                    <div style={{ height: "180px", width: "100%" }}>
                      <svg viewBox="0 0 500 150" style={{ width: "100%", height: "100%", overflow: "visible" }}>
                        <defs>
                          <linearGradient id="purpleGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FD6101" stopOpacity="0.45" />
                            <stop offset="100%" stopColor="#FD6101" stopOpacity="0.0" />
                          </linearGradient>
                          <linearGradient id="violetGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#002247" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#002247" stopOpacity="0.0" />
                          </linearGradient>
                        </defs>
                        <path d="M 0,110 Q 80,40 160,90 T 320,30 T 500,70 L 500,150 L 0,150 Z" fill="url(#purpleGrad)" />
                        <path d="M 0,110 Q 80,40 160,90 T 320,30 T 500,70" fill="none" stroke="#FD6101" strokeWidth="3" />
                        <path d="M 0,130 Q 100,70 200,110 T 400,60 T 500,90 L 500,150 L 0,150 Z" fill="url(#violetGrad)" />
                        <path d="M 0,130 Q 100,70 200,110 T 400,60 T 500,90" fill="none" stroke="#002247" strokeWidth="3" strokeDasharray="4,4" />
                      </svg>
                    </div>
                  </div>

                  {/* Micro KPI Pill Cards Row */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginTop: "24px", paddingTop: "16px", borderTop: "1px solid #f1f5f9" }}>
                    {[
                      { icon: "👛", label: "Storage Capacity", val: `${analytics.storage_used || 0} GB` },
                      { icon: "💜", label: "AI Tokens Used", val: `${((analytics.total_tokens_consumed || 0) / 1000000).toFixed(2)}M` },
                      { icon: "⚡", label: "Pass Rate", val: `${analytics.pass_rate || 0}%` },
                      { icon: "🟧", label: "Active Licenses", val: analytics.active_licenses || 0 }
                    ].map((item, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px", backgroundColor: theme === "dark" ? "#0f172a" : "#f8fafc", padding: "10px 12px", borderRadius: "10px" }}>
                        <span style={{ fontSize: "16px" }}>{item.icon}</span>
                        <div>
                          <span style={{ fontSize: "10px", color: "#64748b", fontWeight: "600", display: "block" }}>{item.label}</span>
                          <span style={{ fontSize: "13px", fontWeight: "800" }}>{item.val}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Donut Distribution Card */}
                <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ width: "100%", textAlign: "left" }}>
                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>Subscription Splits</h3>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>Tenant Plan Distribution</span>
                  </div>

                  {/* Dynamic SVG Donut Chart */}
                  <div style={{ position: "relative", width: "160px", height: "160px", margin: "20px 0" }}>
                    <svg viewBox="0 0 36 36" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                      <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#f1f5f9" strokeWidth="4" />
                      <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#ec4899" strokeWidth="4" strokeDasharray="50 50" strokeDashoffset="0" />
                      <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#8b5cf6" strokeWidth="4" strokeDasharray="30 70" strokeDashoffset="-50" />
                      <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#f59e0b" strokeWidth="4" strokeDasharray="20 80" strokeDashoffset="-80" />
                    </svg>
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                      <span style={{ fontSize: "20px", fontWeight: "900", color: "#ec4899" }}>{organizations.length}</span>
                      <span style={{ fontSize: "10px", display: "block", color: "#64748b", fontWeight: "700" }}>TOTAL TENANTS</span>
                    </div>
                  </div>

                  {/* Dynamic Legend Statistics */}
                  <div style={{ display: "flex", gap: "12px", width: "100%", justifyContent: "space-around" }}>
                    <div>
                      <span style={{ fontSize: "14px", fontWeight: "800", color: "#ec4899" }}>
                        {totalOrgs ? Math.round(((planCounts["Enterprise"] || 0) / totalOrgs) * 100) : 0}%
                      </span>
                      <span style={{ fontSize: "11px", display: "block", color: "#64748b", fontWeight: "600" }}>● Enterprise</span>
                    </div>
                    <div>
                      <span style={{ fontSize: "14px", fontWeight: "800", color: "#8b5cf6" }}>
                        {totalOrgs ? Math.round(((planCounts["Professional"] || 0) / totalOrgs) * 100) : 0}%
                      </span>
                      <span style={{ fontSize: "11px", display: "block", color: "#64748b", fontWeight: "600" }}>● Professional</span>
                    </div>
                    <div>
                      <span style={{ fontSize: "14px", fontWeight: "800", color: "#f59e0b" }}>
                        {totalOrgs ? Math.round((((planCounts["Free"] || 0) + (planCounts["Starter"] || 0)) / totalOrgs) * 100) : 0}%
                      </span>
                      <span style={{ fontSize: "11px", display: "block", color: "#64748b", fontWeight: "600" }}>● Free/Starter</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECOND ROW: 4 Solid Brand Dynamic Cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px" }}>
                {/* 1. Brand Orange Card */}
                <div style={{ background: "#FD6101", borderRadius: "16px", padding: "20px", color: "#ffffff", boxShadow: "0 8px 20px rgba(253,97,1,0.25)", position: "relative", overflow: "hidden" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", opacity: 0.9 }}>Total Organizations</span>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "12px" }}>
                    <span style={{ fontSize: "32px", fontWeight: "900" }}>{organizations.length}</span>
                    <span style={{ fontSize: "12px", fontWeight: "600", opacity: 0.9 }}>Active: {analytics.active_organizations}</span>
                  </div>
                </div>

                {/* 2. Brand Navy Card */}
                <div style={{ background: "#002247", borderRadius: "16px", padding: "20px", color: "#ffffff", boxShadow: "0 8px 20px rgba(0,34,71,0.25)", position: "relative", overflow: "hidden" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", opacity: 0.9 }}>Active User Directory</span>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "12px" }}>
                    <span style={{ fontSize: "32px", fontWeight: "900" }}>{users.length}</span>
                    <span style={{ fontSize: "12px", fontWeight: "600", opacity: 0.9 }}>Online: {analytics.online_users}</span>
                  </div>
                </div>

                {/* 3. Blue Accent Card */}
                <div style={{ background: "#0284c7", borderRadius: "16px", padding: "20px", color: "#ffffff", boxShadow: "0 8px 20px rgba(2,132,199,0.25)", position: "relative", overflow: "hidden" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", opacity: 0.9 }}>Execution Pass Rate</span>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "12px" }}>
                    <span style={{ fontSize: "32px", fontWeight: "900" }}>{analytics.pass_rate}%</span>
                    <span style={{ fontSize: "12px", fontWeight: "600", opacity: 0.9 }}>Runs: {analytics.total_executions}</span>
                  </div>
                </div>

                {/* 4. Slate Dark Card */}
                <div style={{ background: "#1e293b", borderRadius: "16px", padding: "20px", color: "#ffffff", boxShadow: "0 8px 20px rgba(30,41,59,0.25)", position: "relative", overflow: "hidden" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", opacity: 0.9 }}>AI Tokens Cost</span>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "12px" }}>
                    <span style={{ fontSize: "32px", fontWeight: "900" }}>${analytics.ai_cost_estimation}</span>
                    <span style={{ fontSize: "12px", fontWeight: "600", opacity: 0.9 }}>{((analytics.total_tokens_consumed || 0) / 1000000).toFixed(2)}M Tokens</span>
                  </div>
                </div>
              </div>

              {/* THIRD ROW: Recent Audit Activity (Left) + Dark Header Tenant Table (Right) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "24px" }}>
                {/* Recent Activities Feed */}
                <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "700" }}>Recent Administrative Activity</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {auditLogs.slice(0, 5).map((act, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: "12px", paddingBottom: "12px", borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "#ec4899", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "700" }}>
                          ⚡
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: "13px", fontWeight: "700", display: "block" }}>{act.action || act.action_type || "Admin Action"}</span>
                          <span style={{ fontSize: "11px", color: "#64748b" }}>{act.performed_by || act.user_email || act.user || "superadmin@qintra.app"}</span>
                        </div>
                        <span style={{ fontSize: "10.5px", color: "#94a3b8", fontWeight: "600" }}>{act.created_at ? new Date(act.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Recent"}</span>
                      </div>
                    ))}
                    {auditLogs.length === 0 && (
                      <span style={{ fontSize: "13px", color: "#64748b" }}>No administrative audit events recorded yet.</span>
                    )}
                  </div>
                </div>

                {/* Dark Header Organizations Summary Table */}
                <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>Active Tenant Catalog ({organizations.length})</h3>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Registered Customer Organizations</span>
                    </div>
                    <button
                      onClick={() => { setCurrentOrg(null); setOrgModalError(""); setShowOrgModal(true); }}
                      style={{ backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", fontWeight: "700", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <span>+ Add Tenant</span>
                    </button>
                  </div>

                  {/* Dark Header Data Table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#1e293b", color: "#ffffff", textAlign: "left" }}>
                          <th style={{ padding: "12px", borderRadius: "8px 0 0 8px" }}>ORGANIZATION</th>
                          <th style={{ padding: "12px" }}>PLAN</th>
                          <th style={{ padding: "12px" }}>SEATS</th>
                          <th style={{ padding: "12px" }}>STATUS</th>
                          <th style={{ padding: "12px", borderRadius: "0 8px 8px 0", textAlign: "right" }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrgs.slice(0, 5).map((org) => {
                          const statusColor = org.status === "Active" ? "#10b981" : org.status === "Frozen" ? "#06b6d4" : org.status === "Suspended" ? "#ef4444" : "#ec4899";
                          return (
                            <tr key={org.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "12px", fontWeight: "700" }}>
                                {org.name}
                                <span style={{ display: "block", fontSize: "11px", color: "#64748b", fontWeight: "400" }}>{org.owner_email}</span>
                              </td>
                              <td style={{ padding: "12px" }}>
                                <span style={{ backgroundColor: "#fdf2f8", color: "#ec4899", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>
                                  {org.subscription_plan}
                                </span>
                              </td>
                              <td style={{ padding: "12px", fontWeight: "600" }}>{org.user_limit} Users</td>
                              <td style={{ padding: "12px" }}>
                                <span style={{ backgroundColor: `${statusColor}15`, color: statusColor, padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "800" }}>
                                  ● {org.status}
                                </span>
                              </td>
                              <td style={{ padding: "12px", textAlign: "right" }}>
                                <button
                                  onClick={() => { setOverviewOrg(org); setShowOverviewModal(true); }}
                                  style={{ backgroundColor: "transparent", border: "1px solid #FD6101", color: "#FD6101", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer", marginRight: "6px" }}
                                >
                                  <Eye size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Overview
                                </button>
                                <button
                                  onClick={() => { setAddonTargetOrg(org); setShowAddonModal(true); }}
                                  style={{ backgroundColor: "transparent", border: "1px solid #ec4899", color: "#ec4899", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer", marginRight: "6px" }}
                                >
                                  <Zap size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Top-Up
                                </button>
                                <button
                                  onClick={() => { setCurrentOrg(org); setOrgModalError(""); setShowOrgModal(true); }}
                                  style={{ backgroundColor: "transparent", border: "1px solid #cbd5e1", color: "#475569", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: ORGANIZATIONS CONTROL TOWER */}
          {activeTab === "organizations" && (
            <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>Tenant Organizations & Lifecycle Control ({organizations.length})</h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>Manage Enterprise Quotas, Add-on Top-ups, and Account Lifecycles</span>
                </div>
                <button
                  onClick={() => { setCurrentOrg(null); setShowOrgModal(true); }}
                  style={{ backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "10px", fontWeight: "700", fontSize: "13px", cursor: "pointer", boxShadow: "0 4px 12px rgba(236,72,153,0.3)" }}
                >
                  + Provision New Tenant
                </button>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1e293b", color: "#ffffff", textAlign: "left" }}>
                      <th style={{ padding: "12px", borderRadius: "8px 0 0 8px" }}>ID</th>
                      <th style={{ padding: "12px" }}>ORGANIZATION</th>
                      <th style={{ padding: "12px" }}>PLAN</th>
                      <th style={{ padding: "12px" }}>LIMITS (SEATS / STORAGE / TOKENS)</th>
                      <th style={{ padding: "12px" }}>STATUS</th>
                      <th style={{ padding: "12px", borderRadius: "0 8px 8px 0", textAlign: "right" }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.map((org) => {
                      const statusColor = org.status === "Active" ? "#10b981" : org.status === "Frozen" ? "#06b6d4" : org.status === "Suspended" ? "#ef4444" : "#ec4899";
                      return (
                        <tr key={org.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px", fontWeight: "800", color: "#ec4899" }}>#{org.id}</td>
                          <td style={{ padding: "12px", fontWeight: "700" }}>
                            {org.name}
                            <span style={{ display: "block", fontSize: "11px", color: "#64748b", fontWeight: "400" }}>{org.owner_email}</span>
                          </td>
                          <td style={{ padding: "12px" }}>
                            <span style={{ backgroundColor: "#fdf2f8", color: "#ec4899", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "800" }}>
                              {org.subscription_plan}
                            </span>
                          </td>
                          <td style={{ padding: "12px", fontSize: "12px" }}>
                            👤 {org.user_limit} Users | 💾 {org.storage_limit_gb} GB | 🤖 {(org.ai_token_limit / 1000).toFixed(0)}k Tokens
                          </td>
                          <td style={{ padding: "12px" }}>
                            <span style={{ backgroundColor: `${statusColor}15`, color: statusColor, padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "800" }}>
                              ● {org.status}
                            </span>
                          </td>
                          <td style={{ padding: "12px", textAlign: "right" }}>
                            <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                              <button onClick={() => { setOverviewOrg(org); setShowOverviewModal(true); }} style={{ backgroundColor: "transparent", border: "1px solid #FD6101", color: "#FD6101", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}><Eye size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Overview</button>
                              <button onClick={() => { setAddonTargetOrg(org); setShowAddonModal(true); }} style={{ backgroundColor: "transparent", border: "1px solid #ec4899", color: "#ec4899", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}><Zap size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Top-Up</button>
                              <button onClick={() => { setCurrentOrg(org); setShowOrgModal(true); }} style={{ backgroundColor: "transparent", border: "1px solid #cbd5e1", color: "#475569", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>Limits</button>
                              <button onClick={() => handleDownloadTenantBackup(org.id)} style={{ backgroundColor: "transparent", border: "1px solid #8b5cf6", color: "#8b5cf6", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}><Download size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Backup</button>
                              {org.status === "Active" && (
                                <button onClick={() => handleSuspendOrg(org)} style={{ backgroundColor: "transparent", border: "1px solid #ef4444", color: "#ef4444", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}>Suspend</button>
                              )}
                              {org.status === "Suspended" && (
                                <button onClick={() => handleUnsuspendOrg(org)} style={{ backgroundColor: "transparent", border: "1px solid #10b981", color: "#10b981", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}>Unsuspend</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: USER DIRECTORY */}
          {activeTab === "users" && (
            <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>Cross-Organization User Directory ({users.length})</h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>Manage Platform Accounts, Roles, and Authentication</span>
                </div>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#1e293b", color: "#ffffff", textAlign: "left" }}>
                    <th style={{ padding: "12px", borderRadius: "8px 0 0 8px" }}>USER NAME</th>
                    <th style={{ padding: "12px" }}>EMAIL</th>
                    <th style={{ padding: "12px" }}>ORGANIZATION</th>
                    <th style={{ padding: "12px" }}>ROLE</th>
                    <th style={{ padding: "12px" }}>STATUS</th>
                    <th style={{ padding: "12px", borderRadius: "0 8px 8px 0", textAlign: "right" }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((usr, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "12px", fontWeight: "700" }}>{usr.name}</td>
                      <td style={{ padding: "12px" }}>{usr.email}</td>
                      <td style={{ padding: "12px", fontWeight: "600" }}>{usr.organization}</td>
                      <td style={{ padding: "12px" }}>
                        <span style={{ backgroundColor: "#f1f5f9", color: "#475569", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>{usr.role}</span>
                      </td>
                      <td style={{ padding: "12px" }}>
                        <span style={{ backgroundColor: "#10b98115", color: "#10b981", padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "800" }}>● {usr.status || "Active"}</span>
                      </td>
                      <td style={{ padding: "12px", textAlign: "right" }}>
                        <button onClick={() => handleResetPwd(usr.email)} style={{ backgroundColor: "transparent", border: "1px solid #cbd5e1", color: "#475569", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>🔑 Reset Pwd</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 4: ROLES & PERMISSIONS (RBAC MANAGEMENT) */}
          {activeTab === "roles" && (
            <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800" }}>Role-Based Access Control (RBAC) & Permission Matrix</h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>Configure System Roles, Module Capabilities, and Action Level Permissions</span>
                </div>
                <button
                  onClick={() => setShowRoleModal(true)}
                  style={{ backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "10px", fontWeight: "700", fontSize: "13px", cursor: "pointer", boxShadow: "0 4px 12px rgba(236,72,153,0.3)" }}
                >
                  + Add Custom Role
                </button>
              </div>

              {/* Roles Matrix Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1e293b", color: "#ffffff", textAlign: "left" }}>
                      <th style={{ padding: "12px", minWidth: "160px", borderRadius: "8px 0 0 8px" }}>SYSTEM ROLE</th>
                      {permissionKeys.map(pk => (
                        <th key={pk.key} style={{ padding: "12px", textAlign: "center", fontSize: "11px" }}>
                          {pk.label}
                        </th>
                      ))}
                      <th style={{ padding: "12px", borderRadius: "0 8px 8px 0", textAlign: "right" }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(rolesMatrix).map((roleName) => {
                      const perms = rolesMatrix[roleName] || {};
                      const isSystemRole = ["Super Admin", "CEO", "Workspace Owner", "Admin"].includes(roleName);
                      return (
                        <tr key={roleName} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px", fontWeight: "800", color: "#ec4899" }}>
                            {roleName}
                            {isSystemRole && <span style={{ display: "block", fontSize: "10px", color: "#64748b", fontWeight: "600" }}>Built-in System Role</span>}
                          </td>
                          {permissionKeys.map(pk => {
                            const isAllowed = !!perms[pk.key];
                            return (
                              <td key={pk.key} style={{ padding: "12px", textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={isAllowed}
                                  onChange={() => handleTogglePermission(roleName, pk.key, isAllowed)}
                                  style={{ width: "16px", height: "16px", accentColor: "#ec4899", cursor: "pointer" }}
                                />
                              </td>
                            );
                          })}
                          <td style={{ padding: "12px", textAlign: "right" }}>
                            {!isSystemRole ? (
                              <button
                                onClick={() => handleDeleteRole(roleName)}
                                style={{ backgroundColor: "transparent", border: "1px solid #ef4444", color: "#ef4444", padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}
                              >
                                Delete
                              </button>
                            ) : (
                              <span style={{ fontSize: "11px", color: "#94a3b8" }}>Locked</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 5: BILLING & SUBSCRIPTIONS */}
          {activeTab === "billing" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
                <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: "800" }}>Enterprise Subscriptions & Invoicing</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "16px" }}>
                  {subscriptions.map((plan, i) => (
                    <div key={i} style={{ border: plan.is_popular ? "2px solid #ec4899" : "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", position: "relative" }}>
                      {plan.is_popular && <span style={{ position: "absolute", top: "-10px", right: "10px", backgroundColor: "#ec4899", color: "#fff", padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "800" }}>POPULAR</span>}
                      <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "800" }}>{plan.plan_name}</h4>
                      <span style={{ fontSize: "20px", fontWeight: "900", color: "#ec4899" }}>{formatPrice(plan.pricing_monthly_inr || 0)}</span>
                      <span style={{ fontSize: "11px", color: "#64748b" }}> / month</span>
                      <ul style={{ paddingLeft: "16px", fontSize: "12px", margin: "12px 0", color: "#64748b" }}>
                        <li>{plan.max_users} Max Users</li>
                        <li>{plan.max_storage_gb} GB Storage</li>
                        <li>{plan.max_executions} Executions</li>
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* MODALS */}
      {/* 0. Organization Comprehensive Overview Modal */}
      {showOverviewModal && overviewOrg && (() => {
        const orgUsers = users.filter(u =>
          (u.organization && overviewOrg.name && u.organization.toLowerCase() === overviewOrg.name.toLowerCase()) ||
          (u.email && overviewOrg.owner_email && u.email.toLowerCase() === overviewOrg.owner_email.toLowerCase())
        );
        const filteredOrgUsers = orgUsers.filter(u =>
          !overviewUserSearch || (u.name && u.name.toLowerCase().includes(overviewUserSearch.toLowerCase())) || (u.email && u.email.toLowerCase().includes(overviewUserSearch.toLowerCase())) || (u.role && u.role.toLowerCase().includes(overviewUserSearch.toLowerCase()))
        );

        const tokensUsed = overviewOrg.ai_tokens_used || Math.floor(overviewOrg.ai_token_limit * 0.28);
        const tokensLeft = Math.max(0, overviewOrg.ai_token_limit - tokensUsed);
        const tokensLeftPct = Math.round((tokensLeft / overviewOrg.ai_token_limit) * 100);
        const statusColor = overviewOrg.status === "Active" ? "#10b981" : overviewOrg.status === "Frozen" ? "#06b6d4" : overviewOrg.status === "Suspended" ? "#ef4444" : "#ec4899";

        return (
          <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15,23,42,0.75)", backdropFilter: "blur(4px)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
            <div style={{ backgroundColor: theme === "dark" ? "#1e293b" : "#ffffff", color: theme === "dark" ? "#f8fafc" : "#0f172a", borderRadius: "20px", width: "860px", maxWidth: "95%", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)", border: "1px solid #cbd5e1", overflow: "hidden" }}>

              {/* Modal Header */}
              <div style={{ padding: "20px 24px", backgroundColor: theme === "dark" ? "#0f172a" : "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{ width: "44px", height: "44px", borderRadius: "12px", backgroundColor: "#ec4899", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", fontWeight: "800" }}>
                    🏢
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "800" }}>{overviewOrg.name}</h3>
                      <span style={{ backgroundColor: `${statusColor}20`, color: statusColor, padding: "2px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "800" }}>
                        ● {overviewOrg.status || "Active"}
                      </span>
                    </div>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>Tenant ID: #{overviewOrg.id} | Primary Admin: {overviewOrg.owner_email}</span>
                  </div>
                </div>
                <button onClick={() => setShowOverviewModal(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#64748b" }}>✕</button>
              </div>

              {/* Modal Scrollable Body */}
              <div style={{ padding: "24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "20px" }}>
                {/* Top KPI Cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px" }}>
                  <div style={{ backgroundColor: theme === "dark" ? "#0f172a" : "#fdf2f8", border: "1px solid #fbcfe8", borderRadius: "14px", padding: "16px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#ec4899", textTransform: "uppercase", letterSpacing: "0.5px" }}>Subscription Plan</span>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#ec4899", marginTop: "4px" }}>{overviewOrg.subscription_plan}</div>
                    <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginTop: "4px" }}>{overviewOrg.execution_limit?.toLocaleString()} Executions/mo</span>
                  </div>

                  <div style={{ backgroundColor: theme === "dark" ? "#0f172a" : "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "14px", padding: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.5px" }}>Tokens Left</span>
                      <span style={{ fontSize: "11px", fontWeight: "800", color: "#16a34a" }}>{tokensLeftPct}%</span>
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#15803d", marginTop: "4px" }}>
                      {(tokensLeft / 1000).toFixed(0)}k Tokens
                    </div>
                    <div style={{ width: "100%", height: "6px", backgroundColor: "#dcfce7", borderRadius: "4px", marginTop: "8px", overflow: "hidden" }}>
                      <div style={{ width: `${tokensLeftPct}%`, height: "100%", backgroundColor: "#16a34a", borderRadius: "4px" }}></div>
                    </div>
                  </div>

                  <div style={{ backgroundColor: theme === "dark" ? "#0f172a" : "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "14px", padding: "16px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#FD6101", textTransform: "uppercase", letterSpacing: "0.5px" }}>User Seats Quota</span>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#FD6101", marginTop: "4px" }}>
                      {orgUsers.length} / {overviewOrg.user_limit}
                    </div>
                    <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginTop: "4px" }}>{Math.max(0, overviewOrg.user_limit - orgUsers.length)} Available Seats</span>
                  </div>

                  <div style={{ backgroundColor: theme === "dark" ? "#0f172a" : "#faf5ff", border: "1px solid #e9d5ff", borderRadius: "14px", padding: "16px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#9333ea", textTransform: "uppercase", letterSpacing: "0.5px" }}>Storage Limit</span>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#7e22ce", marginTop: "4px" }}>
                      {overviewOrg.storage_limit_gb} GB
                    </div>
                  </div>
                </div>

                {/* Users in this Organization Section */}
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "800" }}>
                        Organization Users & Passwords ({orgUsers.length})
                      </h4>
                      <span style={{ fontSize: "11.5px", color: "#64748b" }}>User accounts, assigned roles, passwords & MFA security status</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Search org users..."
                      value={overviewUserSearch}
                      onChange={(e) => setOverviewUserSearch(e.target.value)}
                      style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "12px", outline: "none", width: "180px" }}
                    />
                  </div>

                  {filteredOrgUsers.length === 0 ? (
                    <div style={{ padding: "24px", textAlign: "center", backgroundColor: theme === "dark" ? "#0f172a" : "#f8fafc", borderRadius: "12px", color: "#64748b", fontSize: "13px" }}>
                      No users currently registered under <strong>{overviewOrg.name}</strong>.
                    </div>
                  ) : (
                    <div style={{ borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#1e293b", color: "#ffffff", textAlign: "left" }}>
                            <th style={{ padding: "10px 12px" }}>USER & EMAIL</th>
                            <th style={{ padding: "10px 12px" }}>ROLE</th>
                            <th style={{ padding: "10px 12px" }}>PASSWORD (SUPER ADMIN)</th>
                            <th style={{ padding: "10px 12px" }}>STATUS & MFA</th>
                            <th style={{ padding: "10px 12px", textAlign: "right" }}>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrgUsers.map((usr, i) => {
                            const isRevealed = !!showPasswordsMap[usr.email];
                            const displayPwd = usr.password || "●●●●●●●●";
                            return (
                              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                <td style={{ padding: "10px 12px", fontWeight: "700" }}>
                                  {usr.name}
                                  <span style={{ display: "block", fontSize: "11px", color: "#64748b", fontWeight: "400" }}>{usr.email}</span>
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <span style={{ backgroundColor: usr.role === "Workspace Owner" ? "#fdf2f8" : usr.role === "Admin" ? "#eff6ff" : "#f1f5f9", color: usr.role === "Workspace Owner" ? "#ec4899" : usr.role === "Admin" ? "#FD6101" : "#475569", padding: "3px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>
                                    {usr.role}
                                  </span>
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontFamily: "monospace", fontSize: "12px", backgroundColor: theme === "dark" ? "#0f172a" : "#f1f5f9", padding: "2px 8px", borderRadius: "4px", fontWeight: "700" }}>
                                      {isRevealed ? displayPwd : "••••••••••••"}
                                    </span>
                                    <button
                                      onClick={() => setShowPasswordsMap(prev => ({ ...prev, [usr.email]: !prev[usr.email] }))}
                                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px" }}
                                    >
                                      {isRevealed ? "🙈" : "👁️"}
                                    </button>
                                  </div>
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <span style={{ color: "#10b981", fontSize: "11px", fontWeight: "800" }}>● {usr.status || "Active"}</span>
                                </td>
                                <td style={{ padding: "10px 12px", textAlign: "right" }}>
                                  <button
                                    onClick={() => handleResetPwd(usr.email)}
                                    style={{ backgroundColor: "transparent", border: "1px solid #cbd5e1", color: "#475569", padding: "3px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
                                  >
                                    🔑 Reset Pwd
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Action Bar Footer */}
              <div style={{ padding: "16px 24px", backgroundColor: theme === "dark" ? "#0f172a" : "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => { setShowOverviewModal(false); setAddonTargetOrg(overviewOrg); setShowAddonModal(true); }}
                    style={{ backgroundColor: "#ec4899", color: "#ffffff", border: "none", padding: "8px 16px", borderRadius: "8px", fontWeight: "700", fontSize: "12px", cursor: "pointer" }}
                  >
                    <Zap size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} /> Top-Up Tokens & Seats
                  </button>
                  <button
                    onClick={() => handleDownloadTenantBackup(overviewOrg.id)}
                    style={{ backgroundColor: "#8b5cf6", color: "#ffffff", border: "none", padding: "8px 16px", borderRadius: "8px", fontWeight: "700", fontSize: "12px", cursor: "pointer" }}
                  >
                    💾 Download Backup
                  </button>
                </div>
                <button onClick={() => setShowOverviewModal(false)} style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "none", padding: "8px 20px", borderRadius: "8px", fontWeight: "700", fontSize: "12px", cursor: "pointer" }}>
                  Close Overview
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* 1. Add-on Top-up Modal */}
      {showAddonModal && addonTargetOrg && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: "#ffffff", color: "#0f172a", borderRadius: "16px", padding: "24px", width: "450px", maxWidth: "90%" }}>
            <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: "800" }}>⚡ Apply Add-on Top-Up Package</h3>
            <span style={{ fontSize: "12px", color: "#64748b" }}>Organization: {addonTargetOrg.name}</span>
            <form onSubmit={handleApplyAddon} style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700" }}>Add-on Category:</label>
              <select value={addonType} onChange={(e) => setAddonType(e.target.value)} style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                <option value="users">Extra User Seats</option>
                <option value="storage">Storage (GB)</option>
                <option value="executions">Execution Credits</option>
                <option value="ai_tokens">AI Tokens</option>
              </select>

              <label style={{ fontSize: "12px", fontWeight: "700" }}>Quantity to Add:</label>
              <input type="number" value={addonQuantity} onChange={(e) => setAddonQuantity(e.target.value)} style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />

              <label style={{ fontSize: "12px", fontWeight: "700" }}>Notes (Optional):</label>
              <input type="text" value={addonNotes} onChange={(e) => setAddonNotes(e.target.value)} placeholder="Reason or reference..." style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />

              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <button type="submit" style={{ flex: 1, backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "10px", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}>Apply Top-Up</button>
                <button type="button" onClick={() => setShowAddonModal(false)} style={{ flex: 1, backgroundColor: "#f1f5f9", color: "#475569", border: "none", padding: "10px", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Organization Edit / Provision Modal */}
      {showOrgModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: "#ffffff", color: "#0f172a", borderRadius: "16px", padding: "24px", width: "500px", maxWidth: "90%" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: "800" }}>{currentOrg ? "Edit Organization Limits" : "Provision New Tenant"}</h3>
            {orgModalError && (
              <div style={{ padding: "10px 14px", borderRadius: "8px", backgroundColor: "#fef2f2", color: "#dc2626", fontSize: "13px", fontWeight: "600", marginBottom: "14px", border: "1px solid #fecaca" }}>
                ⚠️ {orgModalError}
              </div>
            )}
            <form onSubmit={handleOrgSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <input type="text" name="name" defaultValue={currentOrg?.name || ""} placeholder="Organization Name" required style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
              <input type="email" name="owner_email" defaultValue={currentOrg?.owner_email || ""} placeholder="Owner Email" required style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
              <select name="subscription_plan" defaultValue={currentOrg?.subscription_plan || "Professional"} style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                <option value="Free">Free</option>
                <option value="Starter">Starter</option>
                <option value="Professional">Professional</option>
                <option value="Business">Business</option>
                <option value="Enterprise">Enterprise</option>
              </select>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <input type="number" name="user_limit" defaultValue={currentOrg?.user_limit || 10} placeholder="User Seats" style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
                <input type="number" name="storage_limit_gb" defaultValue={currentOrg?.storage_limit_gb || 50} placeholder="Storage (GB)" style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <input type="number" name="ai_token_limit" defaultValue={currentOrg?.ai_token_limit || 1000000} placeholder="AI Tokens" style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
                <input type="number" name="execution_limit" defaultValue={currentOrg?.execution_limit || 2500} placeholder="Executions" style={{ padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <button type="submit" style={{ flex: 1, backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "10px", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}>Save Details</button>
                <button type="button" onClick={() => setShowOrgModal(false)} style={{ flex: 1, backgroundColor: "#f1f5f9", color: "#475569", border: "none", padding: "10px", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Add Custom Role Modal */}
      {showRoleModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: "#ffffff", color: "#0f172a", borderRadius: "16px", padding: "24px", width: "420px", maxWidth: "90%" }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "18px", fontWeight: "800" }}>🛡️ Create Custom System Role</h3>
            <form onSubmit={handleCreateRole} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", marginBottom: "6px" }}>Role Name:</label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Security Auditor, Automation Engineer"
                  required
                  style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
                />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                <button type="submit" style={{ flex: 1, backgroundColor: "#ec4899", color: "#fff", border: "none", padding: "10px", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}>
                  Create Role
                </button>
                <button type="button" onClick={() => setShowRoleModal(false)} style={{ flex: 1, backgroundColor: "#f1f5f9", color: "#475569", border: "none", padding: "10px", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Provisioned Tenant Credentials Modal */}
      {showProvisionedModal && provisionedData && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: "#ffffff", color: "#0f172a", borderRadius: "16px", padding: "28px", width: "520px", maxWidth: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div style={{ fontSize: "40px", marginBottom: "8px" }}>🎉</div>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#1e293b" }}>Organization Provisioned!</h3>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>1st User account created & demo dataset initialized.</p>
            </div>

            <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", marginBottom: "20px", display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b", fontWeight: "600" }}>Organization:</span>
                <span style={{ fontWeight: "700", color: "#0f172a" }}>{provisionedData.name}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b", fontWeight: "600" }}>1st User (Owner):</span>
                <span style={{ fontWeight: "700", color: "#FD6101" }}>{provisionedData.owner_email}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b", fontWeight: "600" }}>Temporary Password:</span>
                <span style={{ fontFamily: "monospace", fontWeight: "800", backgroundColor: "#e2e8f0", padding: "2px 8px", borderRadius: "4px", color: "#0f172a" }}>{provisionedData.temp_password}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b", fontWeight: "600" }}>Tenant Access URL:</span>
                <span style={{ fontFamily: "monospace", fontSize: "12px", color: "#059669" }}>{provisionedData.tenant_url}</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`Workspace: ${provisionedData.name}\nOwner Email: ${provisionedData.owner_email}\nTemporary Password: ${provisionedData.temp_password}\nTenant URL: ${provisionedData.tenant_url}`);
                  showToast("Credentials copied to clipboard!");
                }}
                style={{ flex: 1, backgroundColor: "#FD6101", color: "#ffffff", border: "none", padding: "12px", borderRadius: "8px", fontWeight: "700", cursor: "pointer" }}
              >
                <Copy size={15} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} /> Copy Credentials
              </button>
              <button onClick={() => setShowProvisionedModal(false)} style={{ backgroundColor: "#f1f5f9", color: "#475569", border: "none", padding: "12px 20px", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
